import { z } from 'zod';
import { defineEndpoint } from '../define';

export const ChangelogPrSchema = z.object({
  number: z.number().int(),
  title: z.string(),
  url: z.string(),
  // Absent on snapshots frozen before author capture.
  authorLogin: z.string().nullish(),
  // ISO merge date; null for unmerged PRs, absent on snapshots frozen before date capture.
  mergedAt: z.string().nullish(),
  // ISO creation date; absent on snapshots frozen before capture.
  createdAt: z.string().nullish(),
  // Work-item refs found in this PR's title+body; absent on snapshots frozen before capture.
  refs: z.array(z.string()).nullish(),
  // Head branch name; absent on snapshots frozen before capture.
  headRef: z.string().nullish(),
  // Head commit SHA; absent on snapshots frozen before capture.
  headSha: z.string().nullish(),
});
export type ChangelogPr = z.infer<typeof ChangelogPrSchema>;

export const ChangelogCommitSchema = z.object({
  sha: z.string(),
  subject: z.string(),
  url: z.string(),
  authorLogin: z.string().nullish(),
  // GitHub numeric account id; absent on snapshots frozen before capture.
  authorId: z.number().nullish(),
  // ISO committer date; absent on snapshots frozen before date capture.
  date: z.string().nullish(),
});
export type ChangelogCommit = z.infer<typeof ChangelogCommitSchema>;

export const ChangelogTaskSchema = z.object({
  ref: z.string(),
  source: z.enum(['clickup', 'linear']),
  /** Snapshot metadata frozen at Confirm; empty until task integration resolves it. */
  title: z.string(),
  status: z.string(),
  url: z.string(),
});
export type ChangelogTask = z.infer<typeof ChangelogTaskSchema>;

export const ChangelogSchema = z.object({
  baseSha: z.string(),
  headSha: z.string(),
  baseLabel: z.string(),
  prs: z.array(ChangelogPrSchema),
  commits: z.array(ChangelogCommitSchema),
  tasks: z.array(ChangelogTaskSchema),
  /** Null = live preview; set when the snapshot was frozen (Confirm/Publish). */
  frozenAt: z.string().nullable(),
  /** Lowercased login → app user. Attached at read time, never frozen into snapshots. */
  authorUsers: z.record(z.string(), z.object({ displayName: z.string() })).optional(),
  /**
   * Task ref → latest cached external metadata. Attached at read time from the
   * clickup_tasks / linear_issues caches, never frozen; refs never resolved
   * (404, unconfigured) are simply absent. clickupTaskId only on ClickUp entries.
   */
  taskDetails: z
    .record(
      z.string(),
      z.object({
        clickupTaskId: z.string().optional(),
        title: z.string(),
        status: z.string(),
        url: z.string(),
        refreshedAt: z.string(),
        assignee: z.string().nullish(),
      }),
    )
    .optional(),
});
export type Changelog = z.infer<typeof ChangelogSchema>;

/**
 * Draft: live, refreshable preview (optionally against an explicit diff-baseline
 * release). Confirmed: the frozen Confirm snapshot. `against=published` previews
 * (or returns, once published) the change log vs the nearest lower Published
 * version in the same product+environment.
 */
export const getReleaseChangelog = defineEndpoint({
  method: 'GET',
  path: '/v1/releases/:releaseId/changelog',
  params: z.object({ releaseId: z.string().uuid() }),
  query: z.object({
    baseReleaseId: z.string().uuid().optional(),
    against: z.enum(['baseline', 'published']).default('baseline'),
  }),
  body: z.undefined(),
  response: z.object({ data: ChangelogSchema }),
});

/** Compare any two builds within the same product and environment. */
export const compareReleases = defineEndpoint({
  method: 'GET',
  path: '/v1/releases/compare',
  params: z.object({}),
  query: z.object({
    baseReleaseId: z.string().uuid(),
    headReleaseId: z.string().uuid(),
  }),
  body: z.undefined(),
  response: z.object({ data: ChangelogSchema }),
});
